<template>
  <Head :title="`${t('page.title')} - ${course.code}`" />
  <s-page-block>
    <s-page-header :heading="t('page.title')" class="mb-3">
      <template #actions>
        <s-btn
          v-if="can.viewAnalytics && courseAnalyticsEnabled"
          :href="route('courses.analytics.tasks', {course: course.id})"
          color="secondary"
          icon="poll"
        >
          {{ t('actions.viewAnalytics') }}
        </s-btn>
        <s-key-tooltip keys="m" v-if="can.createAssignments">
          <s-btn
            icon="pencil"
            :href="route('courses.assignments.index.edit', course.id)"
            color="blue-light"
          >
            {{ t('actions.manage') }}
          </s-btn>
        </s-key-tooltip>
        <s-btn
          v-if="can.importAssignments"
          :href="
            route('courses.assignments.import.create', {
              course: course.id,
            })
          "
          color="blue-light"
          icon="import"
        >
          {{ t('actions.importAssignments') }}
        </s-btn>
        <s-key-tooltip keys="n" v-if="can.createAssignments">
          <s-btn icon="plus" :href="route('courses.assignments.create', course.id)">
            {{ t('actions.create') }}
          </s-btn>
        </s-key-tooltip>
      </template>
    </s-page-header>
    <search>
      <div class="relative group mb-3">
        <s-input-field
          id="searchText"
          :placeholder="t('search.placeholder')"
          v-model="form.search"
          autocomplete="off"
          size="sm"
          class="!rounded-full !pl-4 !pr-10"
          :aria-label="t('search.placeholder')"
        />
        <s-icon
          name="magnify"
          class="absolute top-1/2 -translate-y-1/2 right-4 text-blue-500 opacity-70 z-10"
          size="20"
          aria-hidden="true"
        />
      </div>
      <div class="mb-4 flex flex-wrap gap-x-4 gap-y-3 justify-between items-center">
        <s-filter-group>
          <s-date-range-filter
            v-model="form.startDate"
            label-style="after-before"
            :label="t('placeholders.startDate')"
          />
          <s-date-range-filter
            v-model="form.dueDate"
            label-style="after-before"
            :label="t('placeholders.dueDate')"
          />
          <s-multi-select-filter
            v-model="form.visibility"
            :label="t('labels.visibility')"
            :options="[
              {label: t('options.visible'), value: 'visible'},
              {label: t('options.partial'), value: 'partial'},
              {label: t('options.hidden'), value: 'hidden'},
              {label: t('options.notAssigned'), value: 'not_assigned'},
            ]"
          />
          <s-select-filter
            v-model="form.reviewRequired"
            :label="t('labels.reviewRequired')"
            :options="[
              {label: t('options.reviewRequired'), value: 'required'},
              {label: t('options.noReviewRequired'), value: 'not_required'},
            ]"
          />
        </s-filter-group>
        <s-results-count :shown="totalFilteredAssignments" :total="totalAssignments" />
      </div>
    </search>
    <section class="@container flex flex-col gap-5">
      <s-placeholder v-if="!filteredItems.length">
        <template v-if="items.length">
          {{ t('search.noResults') }}
        </template>
        <template v-else>
          {{ t('page.noAssignments') }}
        </template>
      </s-placeholder>
      <ol v-else class="flex flex-col gap-4">
        <li v-for="item in filteredItems" :key="item.id">
          <staff-assignment-card
            v-if="item.type === 'assignment'"
            :course="course"
            :assignment="item"
            :assignmentSpecificPermissions="getAssignmentSpecificPermissions(item.id)"
            :canGrantAssignmentExtensions="can.grantAssignmentExtensions"
          />
          <assignment-module-card v-else-if="item.type === 'module'" :module="item">
            <template #badges>
              <s-badge
                v-if="
                  item.assignments.some(
                    (assignment: StaffCourseAssignmentRowDto) => assignment.isReviewRequired
                  )
                "
                color="blue"
                class="whitespace-nowrap"
              >
                <s-icon name="alert" size="18" class="text-blue-500 opacity-70" />
                {{ t('badges.reviewRequired') }}
              </s-badge>
              <s-badge color="white" class="whitespace-nowrap">
                {{
                  t('badges.totalPoints', {
                    points: item.assignments.reduce(
                      (acc: number, assignment: StaffCourseAssignmentRowDto) =>
                        acc + assignment.pointsAvailable,
                      0
                    ),
                  })
                }}
              </s-badge>
              <s-badge icon="book-edit">
                {{ item.assignments.length }} {{ t('assignment', item.assignments.length) }}
              </s-badge>
            </template>

            <li v-for="assignment in item.assignments" :key="assignment.id">
              <staff-assignment-card
                :course="course"
                :assignment="assignment"
                :assignmentSpecificPermissions="getAssignmentSpecificPermissions(assignment.id)"
                :canGrantAssignmentExtensions="can.grantAssignmentExtensions"
              />
            </li>
          </assignment-module-card>
        </li>
      </ol>
    </section>
  </s-page-block>
</template>
<script setup lang="ts">
import {Head} from '@inertiajs/vue3';
import {route} from 'ziggy-js';
import SPageHeader from '@/design-system/SPageHeader.vue';
import SPlaceholder from '@/design-system/SPlaceholder.vue';
import StaffAssignmentCard from './Index/StaffAssignmentCard.vue';
import AssignmentModuleCard from './Index/AssignmentModuleCard.vue';
import {Course} from '../../types/entities/course';
import SKeyTooltip from '../../design-system/SKeyTooltip.vue';
import SPageBlock from '../../design-system/SPageBlock.vue';
import SBadge from '../../design-system/SBadge.vue';
import SIcon from '../../design-system/SIcon.vue';
import SInputField from '../../design-system/SInputField.vue';
import {useKeyboardShortcuts} from '../../composables/useKeyboardShortcut';
import {router} from '@inertiajs/vue3';
import StaffCourseAssignmentRowDto = App.DTOs.CourseAssignments.StaffCourseAssignmentRowDto;
import SBtn from '../../design-system/SBtn.vue';
import CourseAssignmentPermissionsDto = App.DTOs.CourseAssignments.CourseAssignmentPermissionsDto;
import {useI18n} from 'vue-i18n';
import {computed, reactive, watch} from 'vue';
import SFilterGroup from '../../design-system/SFilterGroup.vue';
import SDateRangeFilter from '../../design-system/filters/SDateRangeFilter.vue';
import SSelectFilter from '../../design-system/filters/SSelectFilter.vue';
import SMultiSelectFilter from '../../design-system/filters/SMultiSelectFilter.vue';
import {usePageParams} from '../../composables/usePageParams';
import SResultsCount from '../../design-system/SResultsCount.vue';

type Item =
  | (StaffCourseAssignmentRowDto & {type: 'assignment'})
  | {
      type: 'module';
      id: number;
      name: string;
      assignments: StaffCourseAssignmentRowDto[];
    };

const {course, items, assignmentSpecificPermissions, can} = defineProps<{
  course: Course;
  items: Item[];
  assignmentSpecificPermissions: CourseAssignmentPermissionsDto[];
  can: {
    grantAssignmentExtensions: boolean;
    createAssignments: boolean;
    importAssignments: boolean;
    viewAnalytics: boolean;
  };
  courseAnalyticsEnabled: boolean;
}>();

const {t} = useI18n({
  useScope: 'local',
  inheritLocale: true,
});

type Visibility = 'visible' | 'partial' | 'hidden' | 'not_assigned';

type Form = {
  search?: string;
  reviewRequired?: 'required' | 'not_required' | null;
  visibility?: Visibility[];
  startDate?: {start?: string; end?: string};
  dueDate?: {start?: string; end?: string};
};

const query = usePageParams<Form>({
  search: '',
  reviewRequired: null,
  visibility: [],
  startDate: {start: '', end: ''},
  dueDate: {start: '', end: ''},
});

const form = reactive<Form>(query);

watch(form, function updateQueryString() {
  const _query: Partial<Form> = {};
  if (form.search) _query.search = form.search;
  if (form.reviewRequired) _query.reviewRequired = form.reviewRequired;
  if (form.visibility?.length) _query.visibility = form.visibility;
  if (form.startDate?.start || form.startDate?.end) {
    _query.startDate = {start: form.startDate.start, end: form.startDate.end};
  }
  if (form.dueDate?.start || form.dueDate?.end) {
    _query.dueDate = {start: form.dueDate.start, end: form.dueDate.end};
  }

  const url = route('courses.assignments.index', {course: course.id, _query});

  window.history.replaceState({}, '', url);
});

const totalAssignments = computed(() => items.reduce(countAssignments, 0));
const totalFilteredAssignments = computed(() => filteredItems.value.reduce(countAssignments, 0));

const countAssignments = (acc: number, item: Item) => {
  if (item.type === 'module') {
    return acc + item.assignments.length;
  }
  return acc + 1;
};

const filteredItems = computed<Item[]>(() => {
  if (
    !form.search &&
    !form.startDate?.start &&
    !form.startDate?.end &&
    !form.dueDate?.start &&
    !form.dueDate?.end &&
    form.reviewRequired === null &&
    !form.visibility?.length
  ) {
    return items;
  }

  return items
    .map((item) => {
      if (item.type === 'module') {
        let filteredModuleAssignments = item.assignments.filter(matchesSearch);

        if (!filteredModuleAssignments.length) {
          return null;
        }

        return {
          ...item,
          assignments: filteredModuleAssignments,
        } as Item;
      }
      return matchesSearch(item) ? item : null;
    })
    .filter(Boolean) as Item[];
});

function matchesSearch(assignment: StaffCourseAssignmentRowDto) {
  return (
    byName(assignment) &&
    byStartDate(assignment) &&
    byDueDate(assignment) &&
    byReviewRequired(assignment) &&
    byVisibility(assignment)
  );
}

function byName(assignment: StaffCourseAssignmentRowDto) {
  const search = form.search?.trim().toLowerCase();

  if (!search) {
    return true;
  }

  const module = findModule(assignment.moduleId);
  const moduleNameMatches = module && module.name.toLowerCase().includes(search);
  const nameMatches = assignment.name.toLowerCase().includes(search);
  return nameMatches || moduleNameMatches;
}

function byReviewRequired(assignment: StaffCourseAssignmentRowDto) {
  if (form.reviewRequired === 'required') {
    return assignment.isReviewRequired;
  }

  if (form.reviewRequired === 'not_required') {
    return !assignment.isReviewRequired;
  }

  return true;
}

function byVisibility(assignment: StaffCourseAssignmentRowDto) {
  if (!form.visibility?.length) {
    return true;
  }
  const visibility = assignment.visibility;

  if (form.visibility?.includes(visibility)) {
    return true;
  }

  const isAssigned =
    assignment.courseLikeAssignmentsInfo.earliestStartDate &&
    assignment.courseLikeAssignmentsInfo.latestDueDate;

  return form.visibility?.includes('not_assigned') && !isAssigned;
}

function byStartDate(assignment: StaffCourseAssignmentRowDto) {
  if (!form.startDate?.start && !form.startDate?.end) {
    return true;
  }
  const startDateString = assignment.courseLikeAssignmentsInfo.earliestStartDate;

  if (!startDateString) {
    return false;
  }

  const startDate = new Date(startDateString);
  const startFilter = form.startDate?.start ? new Date(form.startDate.start as string) : null;
  const endFilter = form.startDate?.end ? new Date(form.startDate.end as string) : null;

  if (startFilter && endFilter) {
    return startDate >= startFilter && startDate <= endFilter;
  } else if (startFilter) {
    return startDate >= startFilter;
  } else if (endFilter) {
    return startDate <= endFilter;
  }
  return true;
}

function byDueDate(assignment: StaffCourseAssignmentRowDto) {
  if (!form.dueDate?.start && !form.dueDate?.end) {
    return true;
  }
  const startDateString = assignment.courseLikeAssignmentsInfo.latestDueDate;

  if (!startDateString) {
    return false;
  }

  const startDate = new Date(startDateString);
  const startFilter = form.dueDate?.start ? new Date(form.dueDate.start as string) : null;
  const endFilter = form.dueDate?.end ? new Date(form.dueDate.end as string) : null;

  if (startFilter && endFilter) {
    return startDate >= startFilter && startDate <= endFilter;
  } else if (startFilter) {
    return startDate >= startFilter;
  } else if (endFilter) {
    return startDate <= endFilter;
  }
  return true;
}

const getAssignmentSpecificPermissions = (assignmentId: number): CourseAssignmentPermissionsDto => {
  return (
    assignmentSpecificPermissions.find(
      (permission) => permission.assignmentId === assignmentId
    ) ?? {
      assignmentId: assignmentId,
      canDelete: false,
      canDuplicate: false,
    }
  );
};

function findModule(moduleId: number | null) {
  return items.find((item) => item.type === 'module' && item.id === moduleId);
}

const keyboardShortcuts = useKeyboardShortcuts();

if (can.createAssignments) {
  keyboardShortcuts.register({
    key: 'n',
    label: t('actions.create'),
    onPress() {
      router.visit(route('courses.assignments.create', course.id));
    },
  });
  keyboardShortcuts.register({
    key: 'm',
    label: t('actions.manage'),
    onPress() {
      router.visit(route('courses.assignments.index.edit', course.id));
    },
  });
}
</script>
<i18n>
{
  "en": {
    "page": {
      "title": "Assignments",
      "noAssignments": "You do not have any assignments at the moment."
    },
    "actions": {
      "manage": "Manage Assignments",
      "create": "Create Assignment",
      "viewAnalytics": "View Analytics"
    },
    "count.results": "Showing {filtered} of {total} assignments",
    "search": {
      "placeholder": "Search assignments by title",
      "noResults": "No assignments found for your search."
    },
    "labels.visibility": "Visibility",
    "labels.reviewRequired": "Review Required",
    "options.visible": "Visible",
    "options.partial": "Partially Visible",
    "options.hidden": "Hidden",
    "options.notAssigned": "Not Assigned",
    "options.reviewRequired": "Yes",
    "options.noReviewRequired": "No",
    "placeholders.startDate": "Start Date",
    "placeholders.dueDate": "Due Date"
  },
  "fr": {
    "page": {
      "title": "Devoirs",
      "noAssignments": "Vous n'avez pas de devoirs pour le moment."
    },
    "actions": {
      "manage": "Gérer les devoirs",
      "create": "Créer un devoir",
      "viewAnalytics": "Voir les analyses"
    },
    "count.results": "Affichage de {filtered} sur {total} devoirs",
    "search": {
      "placeholder": "Rechercher des devoirs par titre",
      "noResults": "Aucun devoir trouvé pour votre recherche."
    },
    "labels.visibility": "Visibilité",
    "labels.reviewRequired": "Révision requise",
    "options.visible": "Visible",
    "options.partial": "Partiellement visible",
    "options.hidden": "Non visible",
    "options.notAssigned": "Non assigné",
    "options.reviewRequired": "Oui",
    "options.noReviewRequired": "Non",
    "placeholders.startDate": "Date de début",
    "placeholders.dueDate": "Date d'échéance"
  }
}
</i18n>
